import {getTopLevelFootprints, getTopLevelLayouts, PcbNodeTypes} from "@buildwithflux/core";
import type {AnyPcbNode, IDocumentData, PcbNodesMap} from "@buildwithflux/models";

/**
 * Pure function to get top-level PCB layout nodes from pcbLayoutNodes map
 * @param pcbLayoutNodes - The PCB layout nodes map
 * @returns Array of top-level layout nodes
 */
export function computeTopLevelPcbLayoutNodes(pcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined) {
    if (!pcbLayoutNodes) {
        return [];
    }
    return getTopLevelLayouts(pcbLayoutNodes);
}

/**
 * Pure function to compute top-level PCB footprint nodes from pcbLayoutNodes
 * @param pcbLayoutNodes The PCB layout nodes map
 * @returns Array of top-level footprint nodes
 */
export function computeTopLevelPcbFootprintNodes(pcbLayoutNodes: PcbNodesMap<AnyPcbNode> | undefined) {
    if (!pcbLayoutNodes) {
        return [];
    }
    return getTopLevelFootprints(pcbLayoutNodes);
}

/**
 * Pure function to check if a PCB node is locked by inheritance (any parent is locked).
 * This recursively checks parent nodes.
 * @param pcbLayoutNodes The PCB layout nodes map
 * @param nodeUid The node UID to check
 * @returns True if the node or any of its parents is locked
 */
export function computeIsLockedByInheritance(
    pcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
    nodeUid: string | undefined,
): boolean {
    if (!nodeUid || !pcbLayoutNodes) {
        return false;
    }

    const node = pcbLayoutNodes[nodeUid];
    if (!node || !node.parentUid) {
        return false;
    }

    if (node.locked) {
        return true;
    }

    return computeIsLockedByInheritance(pcbLayoutNodes, node.parentUid);
}

/**
 * Pure function to compute a map of terminal IDs to net IDs.
 * This is used to look up which net contains a given pad/terminal.
 * @param pcbLayoutNodes The PCB layout nodes map
 * @returns A map of terminal UIDs to their containing net UIDs
 */
export function computeTerminalToNetIdMap(pcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined): {
    [key: string]: string;
} {
    if (!pcbLayoutNodes) {
        return {};
    }

    const terminalToNetIdMap: {[key: string]: string} = {};
    for (const node of Object.values(pcbLayoutNodes)) {
        if (node.type !== PcbNodeTypes.net) {
            continue;
        }
        // Net nodes have a terminals field via PcbNetNodeAdditionalFields
        const terminals = (node as unknown as {terminals?: {[key: string]: unknown}}).terminals;
        if (!terminals) {
            continue;
        }
        for (const terminalId of Object.keys(terminals)) {
            terminalToNetIdMap[terminalId] = node.uid;
        }
    }
    return terminalToNetIdMap;
}
